import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { variables } from '../../config/Variables';
import productCache from '../../services/productCache';
import { formatKatalogKodu } from '../../utils/formatters';
import { 
    IconSearch, 
    IconX, 
    IconLoader, 
    IconChevronRight, 
    IconArrowRight
} from '@tabler/icons-react';

export const QuickSearchInput = ({
    value = '',
    onChange = () => {},
    onSearchSubmit = () => {},
    placeholder = '',
    width = '100%',
    className = ''
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [searchResults, setSearchResults] = useState([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const [isFocused, setIsFocused] = useState(false);

    // Initial cache warm up in background
    useEffect(() => {
        productCache.getProducts();
    }, []);

    // Handle typing and live search
    useEffect(() => {
        const trimmed = value.trim();
        if (trimmed.length >= 3 && trimmed !== '*') {
            setLoading(true);
            let isCurrent = true;

            productCache.filterLocalAll(trimmed).then(allMatches => {
                if (!isCurrent) return;
                setTotalMatches(allMatches.length);
                setSearchResults(allMatches.slice(0, 10)); // Top 10 matches
                if (isFocused) {
                    setIsOpen(true);
                }
                setLoading(false);
                setSelectedIndex(-1);
            });

            return () => {
                isCurrent = false;
            };
        } else {
            setSearchResults([]);
            setTotalMatches(0);
            setIsOpen(false);
            setLoading(false);
            setSelectedIndex(-1);
        }
    }, [value, isFocused]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e) => {
        if (!isOpen || searchResults.length === 0) {
            if (e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(false);
                onSearchSubmit(value);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
                const selected = searchResults[selectedIndex];
                handleSelectProduct(selected);
            } else {
                setIsOpen(false);
                onSearchSubmit(value);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const handleSelectProduct = (product) => {
        setIsOpen(false);
        navigate(`/productdetails/${product.ID || product.Id}`);
    };

    const handleClear = () => {
        onChange({ target: { value: '' } });
        setIsOpen(false);
        if (inputRef.current) inputRef.current.focus();
    };

    const handleSeeAll = () => {
        setIsOpen(false);
        onSearchSubmit(value);
    };

    return (
        <div ref={containerRef} className={`position-relative ${className}`} style={{ width }}>
            {/* Search Input Field */}
            <div className="input-group shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                <span className="input-group-text bg-white border-0 ps-3">
                    {loading ? (
                        <IconLoader className="spin text-primary" size={18} />
                    ) : (
                        <IconSearch size={18} color="#64748b" />
                    )}
                </span>
                <input
                    ref={inputRef}
                    type="text"
                    className="form-control border-0 py-2 fs-6 shadow-none"
                    placeholder={placeholder || t('aranacakMetin', 'Ürün Ara...')}
                    value={value}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setIsFocused(true);
                        if (value.trim().length >= 3 && value.trim() !== '*' && searchResults.length > 0) {
                            setIsOpen(true);
                        }
                    }}
                    onBlur={() => setIsFocused(false)}
                    style={{ color: '#1e293b', fontWeight: 500, backgroundColor: '#ffffff' }}
                />
                {value.length > 0 ? (
                    <button
                        type="button"
                        className="btn btn-white bg-white border-0 text-muted px-2"
                        onClick={handleClear}
                        title="Temizle"
                        style={{ borderRadius: 0 }}
                    >
                        <IconX size={16} />
                    </button>
                ) : null}
                <button 
                    type="button"
                    className="btn btn-primary px-4 fw-semibold d-flex align-items-center gap-1 border-0"
                    onClick={handleSeeAll}
                    style={{ borderRadius: '0 11px 11px 0' }}
                >
                    <span>{t('butonAra', 'Ara')}</span>
                </button>
            </div>

            {/* Live Autocomplete Dropdown Popover */}
            {isOpen && searchResults.length > 0 && (
                <div 
                    className="dropdown-menu show shadow-lg border-0 rounded-4 p-0 mt-2 overflow-hidden w-100 position-absolute"
                    style={{
                        zIndex: 1050,
                        maxHeight: '480px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)'
                    }}
                >
                    {/* Dropdown Header */}
                    <div 
                        className="px-3 py-2 bg-light border-bottom d-flex align-items-center justify-content-between"
                        style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', fontWeight: 700 }}
                    >
                        <span>{t('CanliAramaSonuclari', 'Canlı Arama Sonuçları')}</span>
                        <span className="badge bg-primary rounded-pill px-2 py-1" style={{ fontSize: '10px' }}>
                            {totalMatches} {t('mesajUrunBulundu', 'ürün bulundu')}
                        </span>
                    </div>

                    {/* Results List */}
                    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                        {searchResults.map((prd, idx) => {
                            const isSelected = idx === selectedIndex;
                            return (
                                <div
                                    key={prd.ID || prd.Id || idx}
                                    className="d-flex align-items-center justify-content-between p-2 px-3 border-bottom text-decoration-none"
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#f1f5f9' : '#ffffff',
                                        transition: 'background-color 0.15s ease'
                                    }}
                                    onMouseEnter={() => setSelectedIndex(idx)}
                                    onClick={() => handleSelectProduct(prd)}
                                >
                                    {/* Product Image & Code */}
                                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                        <div 
                                            className="rounded-3 border bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: '44px', height: '44px', overflow: 'hidden' }}
                                        >
                                            <img
                                                src={variables.IMAGE_URL + prd.ImageFile}
                                                alt=""
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            />
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                <span className="fw-bold" style={{ color: '#0f172a', fontSize: '13px' }}>
                                                    {prd.OrjinalKodu}
                                                </span>
                                                <span 
                                                    className="badge" 
                                                    style={{ backgroundColor: '#1e293b', color: '#ffffff', fontSize: '12px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px' }}
                                                >
                                                    {formatKatalogKodu(prd.KatalogKodu)}
                                                </span>
                                            </div>
                                            <div className="text-truncate fw-medium" style={{ fontSize: '12px', color: '#64748b' }}>
                                                {prd.ParsedDetails?.TamAciklama || prd.Kategori || formatKatalogKodu(prd.KatalogKodu)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Badges / Arrow */}
                                    <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                                        {prd.ModelFile && (
                                            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1" style={{ fontSize: '10px' }}>
                                                3D
                                            </span>
                                        )}
                                        {prd.PdfFile && (
                                            <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-2 py-1" style={{ fontSize: '10px' }}>
                                                PDF
                                            </span>
                                        )}
                                        {prd.StepFile && (
                                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1" style={{ fontSize: '10px' }}>
                                                STEP
                                            </span>
                                        )}
                                        <IconChevronRight size={16} color="#94a3b8" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer: See All Button */}
                    <div 
                        className="p-2.5 text-center bg-light border-top"
                        style={{ cursor: 'pointer', transition: 'background-color 0.15s' }}
                        onClick={handleSeeAll}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    >
                        <span className="fw-semibold text-primary d-inline-flex align-items-center gap-1" style={{ fontSize: '13px' }}>
                            <span>{t('TumSonuclariGor', 'Tüm Sonuçları Gör')} ({totalMatches} {t('Kayit', 'Kayıt')})</span>
                            <IconArrowRight size={15} />
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
